import { allAllowedCookies, allAllowedCookiesForPanel, allowedFunctionalCookies } from 'config/allowed-cookies'
import { CookiesArray } from 'models/cookies'
import { getAllowedCookies } from 'services/cookies'
import { isPanelUrl, createUrlWithParams, removeUrlParams, decodeUrlParams, renameQueryParam } from 'utils/urls'

/**
 * Extracts and validates an anchor element from a click event target.
 *
 * This function ensures that only standard navigation links are intercepted.
 * It strictly filters out:
 * - Clicks that did not occur on or inside an anchor tag (`<a>`)
 * - Anchor tags missing the `href` attribute entirely
 * - Specific non-navigational protocols (e.g., 'mailto:', 'tel:', 'javascript:')
 * - Internal page anchors pointing to element IDs (starting with '#')
 *
 * @param {EventTarget | null} target - The raw DOM element that triggered the click event.
 * @returns {HTMLAnchorElement | null} The valid anchor element ready for processing, or null if it should be ignored.
 */
const getValidAnchor = (target: EventTarget | null): HTMLAnchorElement | null => {
  if (!(target instanceof HTMLElement)) return null

  const anchor = target.closest<HTMLAnchorElement>('a')
  if (!anchor) return null

  const rawHref = anchor.getAttribute('href') || ''
  const ignoredPrefixes = ['mailto:', 'tel:', 'javascript:', '#']
  const hasIgnoredPrefix = ignoredPrefixes.some((prefix) => rawHref.startsWith(prefix))

  if (!rawHref || hasIgnoredPrefix) return null

  return anchor
}

/**
 * Handles securely opening links with target="_blank" in a new tab.
 * It removes the `opener` reference for security purposes and handles potential pop-up blockers.
 *
 * @param {HTMLAnchorElement} anchor - The clicked anchor element.
 * @returns {Window | null} The newly opened Window object or null if it's not a _blank target.
 */
const handleBlankTarget = (anchor: HTMLAnchorElement): Window | null => {
  if (anchor.target !== '_blank') return null

  const newWindow = window.open('about:blank', '_blank')
  if (newWindow) {
    newWindow.opener = null
  } else {
    console.warn('Pop-up window was blocked. Redirecting current window instead.')
  }

  return newWindow
}

/**
 * Merges URL parameters from the current page, the target link, and active cookies.
 * Uses a whitelist (allAllowedCookies) to prevent passing navigational parameters (like pagination)
 * from the current URL to the next page.
 *
 * @param {URL} targetUrl - The destination URL object.
 * @param {CookiesArray} cookiesArray - Object containing cookies to append to the URL.
 * @returns {Record<string, any>} A merged object of whitelisted, target, and cookie parameters.
 */
const mergeUrlParams = (targetUrl: URL, cookiesArray: CookiesArray): Record<string, any> => {
  const targetParams = decodeUrlParams(targetUrl.href)
  const currentParams = decodeUrlParams(window.location.href)
  const allowedKeys = allAllowedCookies as string[]

  const whitelistedCurrentParams = Object.keys(currentParams)
    .filter((key) => allowedKeys.includes(key))
    .reduce(
      (obj, key) => {
        obj[key] = currentParams[key]
        return obj
      },
      {} as Record<string, any>,
    )

  // Position of merging is important: currentParams should be overridden
  // by targetParams, which should be overridden by cookiesArray
  const mergedParams = {
    ...whitelistedCurrentParams,
    ...targetParams,
    ...cookiesArray,
  }

  // Remove duplicate parameters
  Object.keys(mergedParams).forEach((key) => {
    if (Array.isArray(mergedParams[key])) {
      mergedParams[key] = [...new Set(mergedParams[key])]
    }
  })

  return mergedParams
}

/**
 * Attaches a global click listener to intercept anchor tag clicks, calculate
 * the appropriate parameters (UTMs, cookies, whitelisted URL params), and dynamically
 * redirect the user to the enriched URL.
 */
const handleConditionalRedirects = (): void => {
  renameQueryParam('clickId', 'uap')

  document.addEventListener('click', async (event) => {
    const $targetAnchor = getValidAnchor(event.target)
    if (!$targetAnchor) return

    event.preventDefault()

    const targetUrl = new URL($targetAnchor.href)
    const newWindow = handleBlankTarget($targetAnchor)

    // Get allowed cookies based on the target URL
    const cookiesArray: CookiesArray = isPanelUrl(targetUrl) ? getAllowedCookies(allAllowedCookiesForPanel) : {}

    // Calculating final parameters
    const mergedParams = mergeUrlParams(targetUrl, cookiesArray)
    const finalUrl = createUrlWithParams(mergedParams, targetUrl)

    // Redirect
    if (newWindow) {
      newWindow.location.href = finalUrl
    } else {
      window.location.href = finalUrl
    }
  })
}

/**
 * Cleans the current page URL by removing functional/non-marketing cookies
 * from the URL string without triggering a page reload.
 */
const clearNonMarketingCookiesFromUrl = (): void => {
  const params = getAllowedCookies(allowedFunctionalCookies)
  const url = removeUrlParams(window.location.href, Object.keys(params))
  window.history.replaceState(null, '', url)
}

/**
 * Initializes the conditional redirects service. It cleans up the initial URL
 * and sets up event listeners for future internal navigations.
 */
export const initHandleConditionalRedirects = (): void => {
  clearNonMarketingCookiesFromUrl()
  handleConditionalRedirects()
}
